import {User, UserManager, WebStorageStateStore} from 'oidc-client';
import {inject, injectable} from 'inversify-props';
import VueRouter from 'vue-router';
import { types, IState } from '@/store';
import {Store} from 'vuex';
import { ISettingService } from '@/services';
import { routePaths } from '@/router/routePaths';
import { eventBus, mcEvents, mcLogger, UniquePromise } from '@/utils';

export interface IAuthService {
  activate(): any;
  isAuthenticated(): Promise<boolean>;
  isLogout: boolean;
  checkRouteAuth(): any;

  startLogin(): any;
  finishLogin(): any;
  finishLoginSilent(): any;

  startLogout(): any;
  finishLogout(): any;
  offlineLogout(): any;
  profile(): any;
  changePassword(): any;

  getUser(): Promise<User | null>;
  getUserManager(): Promise<UserManager | null>;
  getAccessToken(): Promise<string | null>;
}

@injectable()
export class AuthService implements IAuthService {
  @inject() private readonly router!: VueRouter;
  @inject() private readonly store!: Store<IState>;
  @inject() private readonly settingService!: ISettingService;

  private readonly console: Console = mcLogger.getConsole(
    this.constructor.name,
    mcLogger.colors.AuthService
  );

  userManager!: UserManager;
  isLoaded = false;
  isLogout = false;

  /** used as getter since it uses dynamic store values */
  getAuthSettings() {
    return {
      //authority: this.store.state.appConfigSettings.authorityUrl,
      authority: this.store.getters[types.state.appConfigSettings]['authorityUrl'],
      clientId: 'meal_counter_vue',
      serviceScopes: [
        'profile',
        'openid',
        'meal_counter_api',
        'ez_meal_manager_api'
      ],
      hostUrl: (window.location.origin || `${window.location.protocol}//${window.location.host}`),
      MAX_SILENT_RENEW_RETRIES: 3,
      SILENT_REQUEST_TIMEOUT_S: 30
    };
  }

  private readonly activatePromise = new UniquePromise();

  activate() {
    return this.activatePromise.run(async () => {
      if (!this.isLoaded) {
        const userManager = await this.getUserManager();
        // ensure no stale 'state':
        await userManager.clearStaleState();
        await this.checkRouteAuth();
        this.isLoaded = true;
      }
    });
  }

  async checkRouteAuth() {
    const isAuth = await this.isAuthenticated();
    if (isAuth) {
      this.onLogin();
    } else if (this.router.currentRoute.meta?.auth) {
      // don't route to home if the current route doesn't need auth
      // this is particularly important for singin/signout related routes
      this.onSignout();
    }
  }

  private configureUserManager(authSettings: any) {
    const userManager = new UserManager({
      authority: authSettings.authority,
      automaticSilentRenew: true,
      client_id: authSettings.clientId,
      loadUserInfo: true,
      response_type: 'token id_token',
      scope: authSettings.serviceScopes.join(' '),
      silent_redirect_uri: authSettings.hostUrl + '/#/signin-oidc-silent?',
      redirect_uri: authSettings.hostUrl + '/#/signin-oidc?',
      post_logout_redirect_uri: authSettings.hostUrl + '/#/signout-oidc',
      filterProtocolClaims: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      // uses window.setTimeout under the hood, so needs to be ms
      silentRequestTimeout: authSettings.SILENT_REQUEST_TIMEOUT_S * 1000,
      // uses seconds, notably inconsistent with silentRequestTimeout
      accessTokenExpiringNotificationTime: authSettings.SILENT_REQUEST_TIMEOUT_S * authSettings.MAX_SILENT_RENEW_RETRIES
    });

    this.userManager = userManager;

    // handle some events:
    // when the access token has expired
    this.userManager.events.addSilentRenewError((err) => {
      if (err.message === 'Frame window timed out' && document.hidden) {
        // try again when not in background
        const handle = () => {
          if (!document.hidden) {
            this.userManager.signinSilent()
              .then(undefined, innerErr => this.onSilentRenewError());
            // make sure the event only fires once
            document.removeEventListener('visibilitychange', handle);
          }
        };

        document.addEventListener('visibilitychange', handle);
      } else {
        this.onSilentRenewError();
      }
    });
  }

  async getUser() {
    const userManager = await this.getUserManager();
    return userManager.getUser();
  }

  async getAccessToken() {
    const user = await this.getUser();

    let result: string | null = null;
    if (user && !user.expired) {
      result = user.access_token;
    }
    return result;
  }

  async onLogin() {
    const user = await this.getUser();
    if (user) {
      await this.settingService.loadUserInfo();
      this.store.commit(types.mutations.updateAuthState, true);
      eventBus.$emit(mcEvents.loggedIn);
    }
  }

  async onSignout() {
    const authInfo = {
      isAuthenticated: false,
      userInfo: null
    };
    await this.store.dispatch(types.actions.setAuth, authInfo);
    if (this.router.currentRoute.path !== '/') {
      await this.router.push('/');
    }
  }

  onSilentRenewError(retries = 0) {
    const authSettings = this.getAuthSettings();
    if (retries < authSettings.MAX_SILENT_RENEW_RETRIES) {
      this.userManager.signinSilent().then(undefined, () => this.onSilentRenewError(retries + 1));
    } else {
      console.warn(`Silent auth refresh failed for ${retries} attempts. Showing relogin dialog`);
      this.userManager
        .getUser();
    }
  }

  trySilentLogin() {
    return this.userManager
      .signinSilent();
  }

  async isAuthenticated() {
    const isAuth = navigator.onLine ?
      await this.isAuthOnline() :
      await this.isAuthOffline();

    return isAuth;
  }

  private async isAuthOffline(): Promise<boolean> {
    const isAuth = await this.getUser() !== null;
    return isAuth;
  }

  private async isAuthOnline(): Promise<boolean> {
    const userManager = await this.getUserManager();

    const isAuth =
      await userManager
        .getUser()
        .then(user => {
          return (user && !user.expired) ? true : false;
        });
    return isAuth;
  }

  async getUserManager(): Promise<UserManager> {
    // load server settings first since we need some urls
    await this.settingService.activate();

    if (!this.userManager) {
      const authSettings = this.getAuthSettings();
      this.configureUserManager(authSettings);
    }
    return this.userManager;
  }

  async startLogin() {
    const userManager = await this.getUserManager();
    return userManager.signinRedirect();
  }

  async finishLogin() {
    await this.activate();
    const userManager = await this.getUserManager();

    // strip out '/signin-oidc?' from full path
    const url = this.router.currentRoute.fullPath.substr('/signin-oidc?'.length);
    return userManager
      .signinRedirectCallback(url)
      .then(async () => {
        await this.onLogin();
        this.router.push(routePaths.dashboard.path);
      });
  }

  async finishLoginSilent() {
    const userManager = await this.getUserManager();
    return userManager.signinSilentCallback();
  }

  async startLogout() {
    const user = await this.getUser();
    if (user) {
      this.isLogout = true;
      return this.userManager.signoutRedirect();
    }
  }

  async finishLogout() {
    await this.activate();
    const userManager = await this.getUserManager();
    return userManager
      .signoutRedirectCallback()
      .then(() => {
        return this.onSignout();
      });
  }

  async offlineLogout() {
    await this.activate();
    const userManager = await this.getUserManager();
    await userManager.removeUser();
    this.onSignout();
  }

  async profile() {
    const url = await this.getUrl('Profile');
    location.assign(url);
  }

  async changePassword() {
    const url = await this.getUrl('ChangePassword');
    location.assign(url);
  }

  async getUrl(feature: string) {
    const userManager = await this.getUserManager();
    const authSettings = this.getAuthSettings();
    const returnUrl = encodeURIComponent(`${authSettings.hostUrl}/#${routePaths.dashboard.path}`);
    return `${userManager.settings.authority}/Manage/${feature}/?returnUrl=${returnUrl}`;
  }
}

export const authService = new AuthService();
